import React from 'react';
import { Box, Center, Spinner } from '@gluestack-ui/themed';
import { SharedValue } from 'react-native-reanimated';

import { User } from '@/common/api/models/user';
import { JourneyTimeline } from './components/JourneyTimeline';
import { TaskDetailDrawer } from './components/TaskDetailDrawer';
import {
  Module, Task, Level,
} from '../types';
import { useStudyPlan } from '../course/hooks/useStudyPlan';
import { ModuleUpgradeOfferBanner } from './components/ModuleUpgradeOfferBanner';

interface ModuleScreenViewProps {
  module: Module;
  tasks: Task[] | null;
  isModulesLoading: boolean;
  isTasksLoading: boolean;
  selectedTask: Task | null;
  selectedModule: { courseId: string; moduleId: string } | null;
  isMobile: boolean;
  drawerWidth: SharedValue<number>;
  drawerTargetWidth: number;
  levels: Level[];
  totalTasks: number;
  completedCount: number;
  allTasksCompleted: boolean;
  isSubmitting: boolean;
  handleBack: () => void;
  handleConfirmModule: () => void;
  handleSelectTask: (task: Task) => void;
  handleCloseDrawer: () => void;
  handleOpenTask: (task: Task) => void;
  handleModuleUpgraded: () => void;
  user: User;
}

/**
 * ModuleScreenView is the presentation layer for the ModuleScreen.
 * It renders the JourneyTimeline and TaskDetailDrawer.
 */
export const ModuleScreenView: React.FC<ModuleScreenViewProps> = ({
  module,
  tasks,
  isModulesLoading,
  isTasksLoading,
  selectedTask,
  selectedModule,
  isMobile,
  drawerWidth,
  drawerTargetWidth,
  levels,
  totalTasks,
  completedCount,
  allTasksCompleted,
  isSubmitting,
  handleBack,
  handleConfirmModule,
  handleSelectTask,
  handleCloseDrawer,
  handleOpenTask,
  handleModuleUpgraded,
  user,
}) => {
  const { getWeeksForModule, updateModuleWeeks } = useStudyPlan(user, []);

  if (isModulesLoading || isTasksLoading) {
    return (
      <Center testID="loading-spinner" flex={1} bg="$bgApp">
        <Spinner size="large" />
      </Center>
    );
  }

  if (!module) {
    return (
      <Center testID="loading-spinner" flex={1} bg="$bgApp">
        <Spinner size="large" />
      </Center>
    );
  }

  const targetWeeks = getWeeksForModule(module.id);

  const courseId = selectedModule?.courseId || module.courses?.[0]?.id;

  return (
    <Box flex={1} bg="transparent" testID="module-screen-new">
      {module.upgradeOffer && courseId ? (
        <Box px="$4" pt="$3">
          <ModuleUpgradeOfferBanner
            courseId={courseId}
            moduleId={module.id}
            upgradeOffer={module.upgradeOffer}
            onUpgraded={handleModuleUpgraded}
          />
        </Box>
      ) : null}
      <JourneyTimeline
        tasks={tasks || []}
        levels={levels || []}
        selectedTaskId={selectedTask?.id || null}
        onSelectTask={handleSelectTask}
        allTasks={tasks || []}
        module={module}
        targetWeeks={targetWeeks}
        onUpdateWeeks={(weeks) => updateModuleWeeks(module.id, weeks)}
        allTasksCompleted={allTasksCompleted}
        completedCount={completedCount}
        totalTasks={totalTasks}
        onConfirmModule={handleConfirmModule}
        isSubmitting={isSubmitting}
        onBack={handleBack}
        isMobile={isMobile}
      />

      <TaskDetailDrawer
        task={selectedTask}
        tasks={tasks || []}
        module={module}
        selectedModule={selectedModule || { courseId: '', moduleId: '' }}
        isMobile={isMobile}
        drawerWidth={drawerWidth}
        drawerTargetWidth={drawerTargetWidth}
        onClose={handleCloseDrawer}
        onOpenTask={handleOpenTask}
      />
    </Box>
  );
};
